import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { defer, EMPTY, Observable, of, switchMap, take } from "rxjs";
import {
  ApiUrl,
  DeleteOperationPathParams,
  DeleteOperationQueryParams,
  DeleteOperationResponse,
  generateApiURL,
  GetOperationQueryParams,
  PatchOperationPathParams,
  PatchOperationRequestBody,
  PatchOperationResponse,
  PathParamsOfGetOperation,
  PostOperationPathParams,
  PostOperationRequestBody,
  PostOperationResponse,
  PutOperationPathParams,
  PutOperationRequestBody,
  PutOperationResponse,
  ResponseOfGetOperation,
} from "../api/api-url-helper";
import { SitesService } from "./sites";

/**
 * Endpoints that typically return arrays/lists when successful.
 */
const MrListReturningPaths: readonly ApiUrl[] = [
  ApiUrl.APPOINTMENT_STATUSES,
  ApiUrl.MR_DOCKS,
  ApiUrl.MR_DOORS_LOCATIONS,
  ApiUrl.ORDERS,
  ApiUrl.YMS_DOORS_WITH_MR_DOOR_IDS,
];

const restrictedMRPaths: readonly ApiUrl[] = [
  ...MrListReturningPaths,
  ApiUrl.LOCATION_BY_MR_DOOR_NAME,
  ApiUrl.MARK_GATE_IN,
  ApiUrl.MARK_GATE_OUT,
  ApiUrl.MARK_OFF_COMPLEX_WITH_APPOINTMENT,
  ApiUrl.MARK_ON_COMPLEX_WITH_APPOINTMENT,
  ApiUrl.MR_SITE_SETTINGS,
  ApiUrl.ORDER_DETAILS,
];

@Injectable({ providedIn: "root" })
export class RequestService {
  public constructor(
    private readonly httpClient: HttpClient,
    private readonly injector: Injector,
  ) {}

  // TODO: to be implemented
  public apiBuildNumber: string | null = null;

  /**
   * Gets the appropriate default value for the MR restricted endpoint when the site is not a managed receiving site.
   * Returns an empty array for list-returning endpoints, or triggers EMPTY observable for others.
   */
  private getDefaultValueForRestrictedMrPaths<T>(path: ApiUrl): Observable<T> {
    if (MrListReturningPaths.includes(path)) {
      // Return an empty array for endpoints that typically return lists
      return of([] as unknown as T);
    }

    // For endpoints that return objects, single values, or void, return EMPTY
    return EMPTY;
  }

  /**
   * Executes the HTTP request with managed receiving site validation.
   * If the path is restricted and the site is not a managed receiving site,
   * returns appropriate default values based on the endpoint type instead of making the request.
   */
  private executeWithRestrictionCheck<T>(
    path: ApiUrl,
    requestFactory: () => Observable<T>,
  ): Observable<T> {
    // If the endpoint isn't restricted, just perform the request.
    if (!restrictedMRPaths.includes(path)) {
      return requestFactory();
    }

    // Defer fetching until we have the currently-selected site.
    return defer(() => {
      const sites = this.injector.get(SitesService);

      return sites.selectedChanges.pipe(
        take(1),
        switchMap((site) => {
          if (!site.isManagedReceivingSite) {
            // Return appropriate default value based on endpoint return type
            return this.getDefaultValueForRestrictedMrPaths<T>(path);
          }

          return requestFactory();
        }),
      );
    });
  }

  public get<PathName extends ApiUrl>({
    prefix = "",
    path,
    pathParams,
    queryParams,
  }: {
    prefix?: string;
    path: PathName;
    pathParams?: PathParamsOfGetOperation<PathName>;
    queryParams?: GetOperationQueryParams<PathName>;
  }): Observable<ResponseOfGetOperation<PathName>> {
    const apiUrl = generateApiURL<
      PathParamsOfGetOperation<PathName>,
      GetOperationQueryParams<PathName>
    >({
      prefix,
      path,
      pathParams,
      queryParams,
    });

    return this.executeWithRestrictionCheck(path, () =>
      this.httpClient.get<ResponseOfGetOperation<PathName>>(apiUrl),
    );
  }

  public getBlob<PathName extends ApiUrl>({
    prefix = "",
    path,
    pathParams,
    queryParams,
  }: {
    prefix?: string;
    path: PathName;
    pathParams?: PathParamsOfGetOperation<PathName>;
    queryParams?: GetOperationQueryParams<PathName>;
  }): Observable<Blob | null> {
    const apiUrl = generateApiURL<
      PathParamsOfGetOperation<PathName>,
      GetOperationQueryParams<PathName>
    >({
      prefix,
      path,
      pathParams,
      queryParams,
    });

    return this.executeWithRestrictionCheck(path, () =>
      this.httpClient.get(apiUrl, {
        responseType: "blob",
      }),
    );
  }

  public delete<PathName extends ApiUrl>({
    prefix = "",
    path,
    pathParams,
    queryParams,
  }: {
    prefix?: string;
    path: PathName;
    pathParams?: DeleteOperationPathParams<PathName>;
    queryParams?: DeleteOperationQueryParams<PathName>;
  }): Observable<DeleteOperationResponse<PathName>> {
    const apiUrl = generateApiURL<
      DeleteOperationPathParams<PathName>,
      DeleteOperationQueryParams<PathName>
    >({
      prefix,
      path,
      pathParams,
      queryParams,
    });

    return this.executeWithRestrictionCheck(path, () =>
      this.httpClient.delete<DeleteOperationResponse<PathName>>(apiUrl),
    );
  }

  public post<PathName extends ApiUrl>({
    prefix = "",
    path,
    body,
    pathParams,
  }: {
    prefix?: string;
    path: PathName;
    body?: PostOperationRequestBody<PathName>;
    pathParams?: PostOperationPathParams<PathName>;
  }): Observable<PostOperationResponse<PathName>> {
    const apiUrl = generateApiURL({
      prefix,
      path,
      pathParams,
    });

    return this.executeWithRestrictionCheck(path, () =>
      this.httpClient.post<PostOperationResponse<PathName>>(apiUrl, body),
    );
  }

  public patch<PathName extends ApiUrl>({
    prefix = "",
    path,
    body,
    pathParams,
    options,
  }: {
    prefix?: string;
    path: PathName;
    body?: PatchOperationRequestBody<PathName>;
    pathParams?: PatchOperationPathParams<PathName>;
    options?: PatchRequestOptions;
  }): Observable<PatchOperationResponse<PathName>> {
    const apiUrl = generateApiURL({
      prefix,
      path,
      pathParams,
    });

    return this.executeWithRestrictionCheck(path, () =>
      this.httpClient.patch<PatchOperationResponse<PathName>>(apiUrl, body, {
        ...options,
        headers: {
          "Content-Type": "application/merge-patch+json",
          ...(options?.headers ?? {}),
        },
      }),
    );
  }

  public put<PathName extends ApiUrl>({
    prefix = "",
    path,
    body,
    pathParams,
  }: {
    prefix?: string;
    path: PathName;
    body: PutOperationRequestBody<PathName>;
    pathParams?: PutOperationPathParams<PathName>;
  }): Observable<PutOperationResponse<PathName>> {
    const apiUrl = generateApiURL({
      prefix,
      path,
      pathParams,
    });

    return this.executeWithRestrictionCheck(path, () =>
      this.httpClient.put<PutOperationResponse<PathName>>(apiUrl, body),
    );
  }
}

interface PatchRequestOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: "body";
  params?:
    | HttpParams
    | {
        [param: string]:
          | string
          | number
          | boolean
          | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: "json";
  withCredentials?: boolean;
}
